import { Heart } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, SectionHead } from "@/components/Reveal";
import Countdown from "@/components/Countdown";
import DenimBand from "@/components/denim/DenimBand";
import RivetCorners from "@/components/denim/RivetCorners";
import { useWishlist } from "@/components/denim/WishlistContext";
import { HOT_DEALS, productById, inr, lineById } from "@/data/products";

export default function HotDeals() {
    const { has, toggle } = useWishlist();
    const deals = HOT_DEALS.map((d) => ({ ...d, product: productById(d.productId) })).filter((d) => d.product);

    return (
        <DenimBand className="pb-24 pt-20 md:pb-32 md:pt-28" testid="deals-band">
            <div className="mx-auto w-full max-w-[1400px] px-5 md:px-8 lg:px-10">
            <SectionHead
                eyebrow="Ends at Midnight — Every Day"
                title="The Midnight Reset"
                sub="Two pieces, twenty-four hours each. When the clock strikes zero, the offers reset."
                align="center"
            />
            <div className="mt-14 grid gap-6 lg:grid-cols-2">
                {deals.map((d, i) => {
                    const saved = has(d.productId);
                    return (
                        <FadeUp key={d.productId} delay={i * 0.12} className="h-full">
                            <article
                                data-testid={`deal-card-${d.productId}`}
                                className="group grid h-full overflow-hidden border border-white/10 bg-coal/60 transition-shadow duration-500 hover:gold-glow sm:grid-cols-2"
                            >
                                <div className="relative aspect-[4/3] overflow-hidden sm:aspect-auto">
                                    <RivetCorners inset="8px" />
                                    <img
                                        src={d.img}
                                        alt={d.product.name}
                                        loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                    />
                                    <span className="absolute left-4 top-4 bg-gold px-3 py-1 text-[10px] font-mono font-medium uppercase tracking-[0.2em] text-black">
                                        Hot Deal
                                    </span>
                                </div>
                                <div className="flex flex-col p-7 md:p-9">
                                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold">
                                        {lineById(d.product.line)?.name} · Limited Edition
                                    </p>
                                    <h3 className="mt-3 font-display text-xl font-bold leading-snug text-ink md:text-2xl">
                                        {d.product.name}
                                    </h3>
                                    <div className="mt-3 flex items-baseline gap-3">
                                        <span className="font-display text-2xl font-semibold text-gold">
                                            {inr(d.product.now)}
                                        </span>
                                        <span className="text-sm text-mute line-through">{inr(d.product.was)}</span>
                                    </div>
                                    <Countdown prefix={`countdown-${d.productId}`} className="mt-6" />
                                    <div className="mt-auto" />
                                    <button
                                        data-testid={`deal-wishlist-${d.productId}`}
                                        onClick={() => {
                                            const added = toggle({
                                                productId: d.productId,
                                                name: d.product.name,
                                                price: d.product.now,
                                                img: d.img,
                                            });
                                            toast[added ? "success" : "info"](
                                                added
                                                    ? `${d.product.name} saved to your wishlist`
                                                    : `${d.product.name} removed from wishlist`,
                                            );
                                        }}
                                        className={`mt-7 inline-flex w-fit items-center gap-2 border px-6 py-3 text-[11px] font-mono uppercase tracking-[0.2em] transition-colors ${
                                            saved
                                                ? "border-gold bg-gold text-black"
                                                : "border-white/20 text-ink hover:border-gold hover:bg-gold hover:text-black"
                                        }`}
                                    >
                                        <Heart size={14} fill={saved ? "currentColor" : "none"} />
                                        {saved ? "Saved" : "Add to Wishlist"}
                                    </button>
                                </div>
                            </article>
                        </FadeUp>
                    );
                })}
            </div>
            </div>
        </DenimBand>
    );
}
